import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneArray } from "../../utils/undoSnapshots";
import {
    downscaleRasterDataUrlIfNeeded,
    loadImageNaturalSize,
    readFileAsDataUrl,
    LATTICE_RASTER_MAX_EDGE,
} from "../../utils/latticeRasterSource";
import {
    clampRectToBounds,
    normalizeDragRect,
    renderPixelStretchComposite,
    type PixelRect,
    type PixelStretchArtboard,
    type PixelStretchNode,
} from "../../utils/pixelStretch";
import { buildPixelStretchSvg } from "../../utils/pixelStretchSvg";

export type PixelStretchToolMode = "select" | "addRect";

interface PixelStretchState {
    imageDataUrl: string | null;
    imageName: string | null;
    sourceWidth: number | null;
    sourceHeight: number | null;
    imageLoadError: string | null;
    nodes: PixelStretchNode[];
    selectedNodeId: string | null;
    toolMode: PixelStretchToolMode;
    artboardWidth: number;
    artboardHeight: number;
    backgroundColor: string;
    connectorMaxSamples: number;
    showSourceImage: boolean;
    previewVersion: number;
    imageInputRef: React.RefObject<HTMLInputElement>;
    handleImageLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setToolMode: (mode: PixelStretchToolMode) => void;
    setSelectedNodeId: (id: string | null) => void;
    setArtboardWidth: (w: number) => void;
    setArtboardHeight: (h: number) => void;
    setBackgroundColor: (color: string) => void;
    setConnectorMaxSamples: (n: number) => void;
    setShowSourceImage: (v: boolean) => void;
    addNode: (crop: PixelRect, frame?: PixelRect) => void;
    updateNode: (id: string, patch: Partial<Pick<PixelStretchNode, "crop" | "frame">>) => void;
    removeNode: (id: string) => void;
    moveNodeUp: (id: string) => void;
    moveNodeDown: (id: string) => void;
    clearNodes: () => void;
    getDecodedImage: () => HTMLImageElement | null;
    getImageData: () => ImageData | null;
    artboard: PixelStretchArtboard;
    compositeCanvas: HTMLCanvasElement | null;
    buildExportSvgString: () => string;
    handleDownloadPng: () => void;
    handleDownloadSvg: () => void;
    canExport: boolean;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    bumpPreview: () => void;
    pushNodesSnapshot: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const PixelStretchContext = createContext<PixelStretchState | null>(null);

export function usePixelStretch() {
    const ctx = useContext(PixelStretchContext);
    if (!ctx) {
        throw new Error("usePixelStretch must be used within PixelStretchProvider");
    }
    return ctx;
}

let nodeIdCounter = 0;
function nextNodeId(): string {
    nodeIdCounter += 1;
    return `ps-node-${nodeIdCounter}`;
}

interface PixelStretchProviderProps {
    children: React.ReactNode;
}

export function PixelStretchProvider({ children }: PixelStretchProviderProps) {
    const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
    const [imageName, setImageName] = useState<string | null>(null);
    const [sourceWidth, setSourceWidth] = useState<number | null>(null);
    const [sourceHeight, setSourceHeight] = useState<number | null>(null);
    const [imageLoadError, setImageLoadError] = useState<string | null>(null);
    const [nodes, setNodes] = useState<PixelStretchNode[]>([]);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [toolMode, setToolMode] = useState<PixelStretchToolMode>("addRect");
    const [artboardWidth, setArtboardWidth] = useState(800);
    const [artboardHeight, setArtboardHeight] = useState(600);
    const [backgroundColor, setBackgroundColor] = useState("#ffffff");
    const [connectorMaxSamples, setConnectorMaxSamples] = useState(256);
    const [showSourceImage, setShowSourceImage] = useState(true);
    const [previewVersion, setPreviewVersion] = useState(0);

    const imageRef = useRef<HTMLImageElement | null>(null);
    const imageDataRef = useRef<ImageData | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;

    const history = useUndoRedo<PixelStretchNode[]>({ clone: cloneArray });

    const pushNodesSnapshot = useCallback(() => {
        history.push(nodesRef.current);
    }, [history]);

    const bumpPreview = useCallback(() => {
        setPreviewVersion((v) => v + 1);
    }, []);

    const decodeImage = useCallback(async (dataUrl: string) => {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Could not decode image."));
            img.src = dataUrl;
        });
        imageRef.current = img;
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Could not create canvas context.");
        ctx.drawImage(img, 0, 0);
        imageDataRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        bumpPreview();
    }, [bumpPreview]);

    const handleImageLoad = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            setImageLoadError(null);
            try {
                let dataUrl = await readFileAsDataUrl(file);
                const size = await loadImageNaturalSize(dataUrl);
                const scaled = await downscaleRasterDataUrlIfNeeded(
                    dataUrl,
                    size.w,
                    size.h,
                    LATTICE_RASTER_MAX_EDGE,
                );
                dataUrl = scaled.dataUrl;
                setImageDataUrl(dataUrl);
                setImageName(file.name);
                setSourceWidth(scaled.w);
                setSourceHeight(scaled.h);
                setArtboardWidth(scaled.w);
                setArtboardHeight(scaled.h);
                setNodes([]);
                setSelectedNodeId(null);
                history.clear();
                await decodeImage(dataUrl);
            } catch (err) {
                setImageLoadError(
                    err instanceof Error ? err.message : "Failed to load image.",
                );
            }
        },
        [decodeImage, history],
    );

    const artboard: PixelStretchArtboard = useMemo(
        () => ({
            width: artboardWidth,
            height: artboardHeight,
            backgroundColor,
        }),
        [artboardWidth, artboardHeight, backgroundColor],
    );

    const compositeCanvas = useMemo(() => {
        void previewVersion;
        const img = imageRef.current;
        const data = imageDataRef.current;
        if (!img || !data || nodes.length === 0) return null;
        return renderPixelStretchComposite(img, data, nodes, artboard, {
            connectorMaxSamples,
            transparentBackground: showSourceImage,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        nodes,
        artboard,
        connectorMaxSamples,
        showSourceImage,
        previewVersion,
        imageDataUrl,
    ]);

    useEffect(() => {
        bumpPreview();
    }, [nodes, artboard, connectorMaxSamples, bumpPreview]);

    const undo = useCallback(() => {
        history.undo(nodesRef.current, (snap) => {
            setNodes(snap);
            setSelectedNodeId((sid) =>
                sid && snap.some((n) => n.id === sid) ? sid : null,
            );
        });
    }, [history]);

    const redo = useCallback(() => {
        history.redo(nodesRef.current, (snap) => {
            setNodes(snap);
            setSelectedNodeId((sid) =>
                sid && snap.some((n) => n.id === sid) ? sid : null,
            );
        });
    }, [history]);

    const addNode = useCallback(
        (crop: PixelRect, frame?: PixelRect) => {
            if (!sourceWidth || !sourceHeight) return;
            pushNodesSnapshot();
            const c = clampRectToBounds(crop, sourceWidth, sourceHeight);
            const f = frame
                ? frame
                : { x: c.x, y: c.y, w: c.w, h: c.h };
            const node: PixelStretchNode = {
                id: nextNodeId(),
                crop: { ...c },
                frame: { ...f },
            };
            setNodes((prev) => [...prev, node]);
            setSelectedNodeId(node.id);
            setToolMode("select");
        },
        [sourceWidth, sourceHeight, pushNodesSnapshot],
    );

    const updateNode = useCallback(
        (id: string, patch: Partial<Pick<PixelStretchNode, "crop" | "frame">>) => {
            setNodes((prev) =>
                prev.map((n) => (n.id === id ? { ...n, ...patch } : n)),
            );
        },
        [],
    );

    const removeNode = useCallback((id: string) => {
        pushNodesSnapshot();
        setNodes((prev) => prev.filter((n) => n.id !== id));
        setSelectedNodeId((sid) => (sid === id ? null : sid));
    }, [pushNodesSnapshot]);

    const moveNodeUp = useCallback((id: string) => {
        pushNodesSnapshot();
        setNodes((prev) => {
            const i = prev.findIndex((n) => n.id === id);
            if (i <= 0) return prev;
            const next = [...prev];
            [next[i - 1], next[i]] = [next[i], next[i - 1]];
            return next;
        });
    }, [pushNodesSnapshot]);

    const moveNodeDown = useCallback((id: string) => {
        pushNodesSnapshot();
        setNodes((prev) => {
            const i = prev.findIndex((n) => n.id === id);
            if (i < 0 || i >= prev.length - 1) return prev;
            const next = [...prev];
            [next[i], next[i + 1]] = [next[i + 1], next[i]];
            return next;
        });
    }, [pushNodesSnapshot]);

    const clearNodes = useCallback(() => {
        if (nodesRef.current.length === 0) return;
        pushNodesSnapshot();
        setNodes([]);
        setSelectedNodeId(null);
    }, [pushNodesSnapshot]);

    const getDecodedImage = useCallback(() => imageRef.current, []);
    const getImageData = useCallback(() => imageDataRef.current, []);

    const canExport = Boolean(imageDataUrl && nodes.length > 0);

    const buildExportSvgString = useCallback(() => {
        if (!imageDataUrl || !sourceWidth || !sourceHeight) return "";
        return buildPixelStretchSvg(
            { dataUrl: imageDataUrl, w: sourceWidth, h: sourceHeight },
            nodes,
            artboard,
            { connectorMaxSamples },
        );
    }, [
        imageDataUrl,
        sourceWidth,
        sourceHeight,
        nodes,
        artboard,
        connectorMaxSamples,
    ]);

    const handleDownloadPng = useCallback(() => {
        const canvas =
            imageRef.current && imageDataRef.current
                ? renderPixelStretchComposite(
                      imageRef.current,
                      imageDataRef.current,
                      nodes,
                      artboard,
                      { connectorMaxSamples, transparentBackground: false },
                  )
                : null;
        if (!canvas) return;
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `pixel-stretch-${Date.now()}.png`;
        a.click();
    }, [compositeCanvas, nodes, artboard, connectorMaxSamples]);

    const handleDownloadSvg = useCallback(() => {
        const svg = buildExportSvgString();
        if (!svg) return;
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `pixel-stretch-${Date.now()}.svg`;
        a.click();
        URL.revokeObjectURL(a.href);
    }, [buildExportSvgString]);

    const getPresetValues = useCallback(
        () => ({
            artboardWidth,
            artboardHeight,
            backgroundColor,
            connectorMaxSamples,
            showSourceImage,
            toolMode,
            nodes: nodes.map((n) => ({
                id: n.id,
                crop: n.crop,
                frame: n.frame,
            })),
        }),
        [
            artboardWidth,
            artboardHeight,
            backgroundColor,
            connectorMaxSamples,
            showSourceImage,
            toolMode,
            nodes,
        ],
    );

    const applyPreset = useCallback(
        (values: Record<string, unknown>) => {
            if (typeof values.artboardWidth === "number") {
                setArtboardWidth(values.artboardWidth);
            }
            if (typeof values.artboardHeight === "number") {
                setArtboardHeight(values.artboardHeight);
            }
            if (typeof values.backgroundColor === "string") {
                setBackgroundColor(values.backgroundColor);
            }
            if (typeof values.connectorMaxSamples === "number") {
                setConnectorMaxSamples(values.connectorMaxSamples);
            }
            if (typeof values.showSourceImage === "boolean") {
                setShowSourceImage(values.showSourceImage);
            }
            if (
                values.toolMode === "select" ||
                values.toolMode === "addRect"
            ) {
                setToolMode(values.toolMode);
            }
            if (Array.isArray(values.nodes)) {
                const restored: PixelStretchNode[] = [];
                for (const raw of values.nodes) {
                    if (
                        raw &&
                        typeof raw === "object" &&
                        typeof (raw as PixelStretchNode).id === "string" &&
                        (raw as PixelStretchNode).crop &&
                        (raw as PixelStretchNode).frame
                    ) {
                        const n = raw as PixelStretchNode;
                        restored.push({
                            id: n.id,
                            crop: { ...n.crop },
                            frame: { ...n.frame },
                        });
                    }
                }
                setNodes(restored);
            }
        },
        [],
    );

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "pixel-stretch",
        defaultPreset: {
            artboardWidth: 800,
            artboardHeight: 600,
            backgroundColor: "#ffffff",
            connectorMaxSamples: 256,
            showSourceImage: true,
            toolMode: "addRect",
            nodes: [],
        },
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
        if (sourceWidth && sourceHeight) {
            setArtboardWidth(sourceWidth);
            setArtboardHeight(sourceHeight);
        }
    }, [resetStoredParameters, sourceWidth, sourceHeight, history]);

    return (
        <PixelStretchContext.Provider
            value={{
                imageDataUrl,
                imageName,
                sourceWidth,
                sourceHeight,
                imageLoadError,
                nodes,
                selectedNodeId,
                toolMode,
                artboardWidth,
                artboardHeight,
                backgroundColor,
                connectorMaxSamples,
                showSourceImage,
                previewVersion,
                imageInputRef,
                handleImageLoad,
                setToolMode,
                setSelectedNodeId,
                setArtboardWidth,
                setArtboardHeight,
                setBackgroundColor,
                setConnectorMaxSamples,
                setShowSourceImage,
                addNode,
                updateNode,
                removeNode,
                moveNodeUp,
                moveNodeDown,
                clearNodes,
                getDecodedImage,
                getImageData,
                artboard,
                compositeCanvas,
                buildExportSvgString,
                handleDownloadPng,
                handleDownloadSvg,
                canExport,
                applyPreset,
                getPresetValues,
                resetParameters,
                bumpPreview,
                pushNodesSnapshot,
                undo,
                redo,
                canUndo: history.canUndo,
                canRedo: history.canRedo,
            }}
        >
            {children}
        </PixelStretchContext.Provider>
    );
}

export { normalizeDragRect, clampRectToBounds };
